"use client";

import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { cn, scrollToId } from "@/lib/utils";
import { DOCS } from "@/lib/constants";
import {
  SELECT_MOBILE_PLAN_EVENT,
  type MobilePlan,
} from "@/components/sections/products/mobile-proxies/pricing-cards/plan-event";
import { buttonVariants } from "@/components/ui/button";
import SectionHeader from "@/components/ui/section-header";
import {
  ArrowRight,
  BarChart3,
  Check,
  Database,
  Globe,
  Layers,
  MapPin,
  RefreshCw,
  ShieldCheck,
  SlidersHorizontal,
  Users,
} from "@/components/icons";

const models = [
  {
    name: "Per GB",
    eyebrow: "Flexible access points",
    accent: "var(--brand-green)",
    icon: Database,
    headline: "Pay only for the data you use",
    description:
      "Tap into the full proxy pool through unlimited access points that share one data balance — no fixed proxy count to manage.",
    bestFor: "Web scraping, ad verification, regional SEO testing, and variable campaigns.",
    features: [
      "Unlimited access points on one shared balance",
      "Sticky or random IP behavior per access point",
      "City-level targeting or random city rotation",
      "Live credentials, cURL examples, and proxy list exports",
    ],
    cta: "See Per GB Plans",
    plan: "per-gb" as MobilePlan,
    docsHref: DOCS.mobilePerGbPlan,
    docsLabel: "Per GB docs",
  },
  {
    name: "Per Proxy",
    eyebrow: "From $59/proxy/month",
    accent: "var(--brand-green)",
    icon: Users,
    headline: "Full control over every exit",
    description:
      "Each proxy is a real mobile IP assigned to your account, with its own credentials, location, ISP, rotation, and usage visibility. Plans start at $59 per proxy per month.",
    bestFor: "Long-running sessions, e-commerce monitoring, cybersecurity testing, and resale.",
    features: [
      "Lock proxies to a city or route through the fastest location",
      "ISP preference with per-proxy speed and IPv4 visibility",
      "Rotate manually, on a custom interval, or via rotation URL",
      "Per-proxy passwords, proxy formats, and data usage tracking",
    ],
    cta: "See Per Proxy Plans",
    plan: "per-proxy" as MobilePlan,
    docsHref: DOCS.perProxyPlan,
    docsLabel: "Per Proxy docs",
  },
];

const controls = [
  { icon: MapPin, label: "City lock or Fastest" },
  { icon: Globe, label: "ISP preference" },
  { icon: SlidersHorizontal, label: "DNS or IP hostname" },
  { icon: ShieldCheck, label: "Password or whitelist auth" },
  { icon: RefreshCw, label: "Sticky, random, or timed rotation" },
  { icon: BarChart3, label: "Global Analytics and logs" },
];

const poolingSteps = [
  { request: "Request 1", exit: "Proxy 1" },
  { request: "Request 2", exit: "Proxy 2" },
  { request: "Request 3", exit: "Proxy 3" },
  { request: "Request 4", exit: "Proxy 4" },
  { request: "Request 5", exit: "Proxy 5" },
  { request: "Request 6", exit: "Proxy 1", marker: "Looping" },
  { request: "Request 7", exit: "Proxy 2" },
];

function selectPlan(plan: MobilePlan) {
  window.dispatchEvent(new CustomEvent(SELECT_MOBILE_PLAN_EVENT, { detail: plan }));
  scrollToId("pricing-plans");
}

export default function MobileProductModels() {
  const prefersReducedMotion = useReducedMotion();

  return (
    <section
      id="mobile-product-models"
      className="py-section border-border border-b"
      aria-labelledby="mobile-product-models-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 max-w-3xl">
          <SectionHeader
            eyebrow="Product Options"
            id="mobile-product-models-heading"
            heading="Choose by how much control you need"
            body="Per GB gives you flexible access points on a shared data balance. Per Proxy gives you real mobile IPs you control directly. Both run on the same premium mobile network."
          />
        </div>

        <div className="grid gap-5 lg:grid-cols-[1.05fr_0.95fr]">
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={scrollRevealViewport}
            transition={{ duration: 0.45 }}
            className="border-border bg-muted/20 relative overflow-hidden rounded-2xl border p-6 sm:p-8"
          >
            <div className="relative flex h-full flex-col">
              <div className="mb-8 flex items-center justify-between gap-4">
                <div className="border-border bg-background flex h-12 w-12 items-center justify-center rounded-xl border">
                  <Layers size={22} style={{ color: "var(--brand-green)" }} aria-hidden="true" />
                </div>
                <span
                  className="border-border bg-background rounded-full border px-3 py-1 text-xs font-semibold"
                  style={{ color: "var(--brand-green)" }}
                >
                  Per Proxy feature — Pooling
                </span>
              </div>

              <h3 className="text-foreground max-w-xl text-xl leading-tight font-semibold sm:text-2xl">
                Per GB simplicity with Per Proxy control.
              </h3>
              <p className="text-muted-foreground mt-5 max-w-2xl leading-relaxed sm:text-lg">
                Pooling lets you take all or a subset of your Per Proxy subscriptions and expose
                them through one endpoint. Your application connects once, while Proxidize cycles
                requests through the selected exits — any change you make to a proxy reflects in the
                pool instantly.
              </p>

              <div className="mt-8 overflow-hidden rounded-xl border border-[#27272a] bg-[#0a0a0b]">
                <div className="flex items-center gap-1.5 border-b border-[#27272a] px-4 py-2.5">
                  <span className="h-2 w-2 rounded-full bg-[#3f3f46]" aria-hidden="true" />
                  <span className="h-2 w-2 rounded-full bg-[#3f3f46]" aria-hidden="true" />
                  <span className="h-2 w-2 rounded-full bg-[#3f3f46]" aria-hidden="true" />
                  <span className="ml-2 font-mono text-[10px] text-[#a1a1aa]">
                    one endpoint — five exits behind it
                  </span>
                </div>
                <div className="space-y-1 overflow-x-auto p-4 font-mono text-xs leading-relaxed">
                  <p className="whitespace-nowrap text-[#a1a1aa]">
                    <span className="text-[#71717a] select-none">$ </span>
                    curl -x <span className="text-[#f7f7f8]">pool.proxidize.com:31112</span>{" "}
                    ipinfo.io/ip
                  </p>
                  <p className="whitespace-nowrap text-[#5b8cff]">
                    174.220.11.42
                    <span className="text-[#71717a]"> — Proxy 1</span>
                  </p>
                  <p className="whitespace-nowrap text-[#a1a1aa]">
                    <span className="text-[#71717a] select-none">$ </span>
                    curl -x <span className="text-[#f7f7f8]">pool.proxidize.com:31112</span>{" "}
                    ipinfo.io/ip
                  </p>
                  <p className="whitespace-nowrap text-[#5b8cff]">
                    172.58.99.103
                    <span className="text-[#71717a]"> — Proxy 2, same command</span>
                  </p>
                </div>
              </div>

              <div className="mt-8 grid gap-x-6 gap-y-3 sm:grid-cols-2">
                {controls.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.label}
                      className="text-muted-foreground flex items-center gap-2.5 text-sm"
                    >
                      <Icon
                        size={15}
                        className="shrink-0"
                        style={{ color: "var(--brand-green)" }}
                        aria-hidden="true"
                      />
                      {item.label}
                    </div>
                  );
                })}
              </div>

              <div
                className="mt-8 space-y-2"
                aria-label="Pooling example: seven requests cycling through five proxies"
              >
                {poolingSteps.map((step, index) => (
                  <motion.div
                    key={step.request}
                    initial={prefersReducedMotion ? false : { opacity: 0, x: -14 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={scrollRevealViewport}
                    transition={{
                      duration: 0.28,
                      delay: prefersReducedMotion ? 0 : index * 0.12,
                    }}
                    className={cn(
                      "border-foreground/10 bg-foreground/[0.035] grid grid-cols-[92px_1fr_80px] items-center gap-3 rounded-xl border px-3",
                      step.marker ? "py-4" : "py-2.5"
                    )}
                  >
                    <span className="text-muted-foreground text-sm">{step.request}</span>
                    <div className={cn("grid", step.marker ? "gap-2.5" : "gap-1.5")}>
                      {step.marker && (
                        <span
                          className="text-center text-[10px] leading-none font-bold tracking-[0.18em] uppercase"
                          style={{ color: "var(--brand-green)" }}
                        >
                          {step.marker}
                        </span>
                      )}
                      <motion.span
                        className="h-1 rounded-full"
                        style={{
                          backgroundColor:
                            "color-mix(in srgb, var(--brand-green) 50%, transparent)",
                        }}
                        initial={prefersReducedMotion ? false : { scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        viewport={scrollRevealViewport}
                        transition={{
                          duration: 0.35,
                          delay: prefersReducedMotion ? 0 : index * 0.12 + 0.1,
                        }}
                      />
                    </div>
                    <span className="text-foreground text-sm font-bold">{step.exit}</span>
                  </motion.div>
                ))}
              </div>

              <div className="mt-auto flex flex-wrap gap-3 pt-8">
                <button
                  type="button"
                  onClick={() => scrollToId("pricing-plans")}
                  className={cn(
                    buttonVariants({ size: "lg" }),
                    "bg-primary text-primary-foreground font-semibold"
                  )}
                >
                  Compare Plans
                  <ArrowRight size={15} className="ml-1.5" aria-hidden="true" />
                </button>
              </div>
            </div>
          </motion.div>

          <div className="grid gap-5">
            {models.map((model, index) => {
              const Icon = model.icon;

              return (
                <motion.article
                  key={model.name}
                  initial={prefersReducedMotion ? false : { opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={scrollRevealViewport}
                  transition={{ duration: 0.45, delay: index * 0.08 }}
                  className="border-border bg-muted/20 relative flex flex-col overflow-hidden rounded-2xl border p-6 sm:p-7"
                >
                  <div className="relative mb-5 flex items-center justify-between gap-4">
                    <div
                      className="border-border bg-background flex h-11 w-11 items-center justify-center rounded-xl border"
                      style={{ color: model.accent }}
                    >
                      <Icon size={20} aria-hidden="true" />
                    </div>
                    <span
                      className="border-border bg-background rounded-full border px-3 py-1 text-xs font-semibold"
                      style={{ color: model.accent }}
                    >
                      {model.eyebrow}
                    </span>
                  </div>

                  <h3 className="text-foreground relative text-xl font-semibold sm:text-2xl">
                    Mobile {model.name}
                  </h3>
                  <p
                    className="relative mt-1.5 text-sm font-semibold"
                    style={{ color: model.accent }}
                  >
                    {model.headline}
                  </p>
                  <p className="text-muted-foreground relative mt-4 text-sm leading-relaxed sm:text-base">
                    {model.description}
                  </p>

                  <ul
                    className="relative mt-5 grid gap-2"
                    aria-label={`Mobile ${model.name} features`}
                  >
                    {model.features.map((feature) => (
                      <li
                        key={feature}
                        className="text-muted-foreground flex items-start gap-2.5 text-sm"
                      >
                        <Check
                          size={14}
                          className="mt-0.5 shrink-0"
                          style={{ color: model.accent }}
                          aria-hidden="true"
                        />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <p className="text-foreground/80 relative mt-4 text-sm leading-relaxed">
                    <span className="text-foreground font-semibold">Best for: </span>
                    {model.bestFor}
                  </p>

                  <div className="relative mt-auto flex flex-wrap items-center gap-x-5 gap-y-2 pt-5">
                    <button
                      type="button"
                      onClick={() => selectPlan(model.plan)}
                      className="text-foreground inline-flex items-center gap-1.5 text-sm font-semibold transition-opacity hover:opacity-80"
                    >
                      {model.cta}
                      <ArrowRight size={14} aria-hidden="true" />
                    </button>
                    <a
                      href={model.docsHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm font-medium transition-colors"
                    >
                      {model.docsLabel}
                      <ArrowRight size={13} aria-hidden="true" />
                    </a>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
